var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { includes, invariantImage as invariant, isNumber, shouldBeOneOf, startsWith, } from '../utils';
var formatOptions = [
    'jpg',
    'jpe',
    'jpeg',
    'jpc',
    'jp2',
    'j2k',
    'wdp',
    'jxr',
    'hdp',
    'png',
    'gif',
    'webp',
    'bmp',
    'tif',
    'tiff',
    'ico',
    'pdf',
    'ps',
    'ept',
    'eps',
    'eps3',
    'psd',
    'svg',
    'ai',
    'djvu',
    'flif',
];
var cropOptions = [
    'scale',
    'fit',
    'limit',
    'mfit',
    'fill',
    'lfill',
    'pad',
    'lpad',
    'mpad',
    'crop',
    'thumb',
    'imagga_crop',
    'imagga_scale',
];
var gravityOptions = [
    'north_west',
    'north',
    'north_east',
    'west',
    'center',
    'east',
    'south_west',
    'south',
    'south_east',
    'xy_center',
    'face',
    'face:center',
    'face:auto',
    'faces',
    'faces:center',
    'faces:auto',
    'body',
    'body:face',
    'ocr_text',
    'adv_face',
    'adv_faces',
    'adv_eyes',
    'custom',
    'custom:face',
    'custom:faces',
    'custom:adv_face',
    'custom:adv_faces',
];
var qualityOptions = ['auto', 'auto:best', 'auto:good', 'auto:eco', 'auto:low', 'jpegmini'];
var angleOptions = ['auto_right', 'auto_left', 'ignore', 'vflip', 'hflip'];
var backgroundOptions = [
    'auto:border',
    'auto:predominant',
    'auto:border_contrast',
    'auto:predominant_contrast',
];
var colorRegex = /^(?:[a-z]+$|rgb:(?:[0-9a-f]{3,4}$|[0-9a-f]{6}$|[0-9a-f]{8}$))/i;
var colorSpaceOptions = ['srgb', 'tinysrgb', 'no_cmyk'];
var flagOptions = [
    'any_format',
    'attachment',
    'apng',
    'awebp',
    'clip',
    'clip_evenodd',
    'cutter',
    'force_strip',
    'ignore_aspect_ratio',
    'immutable_cache',
    'keep_attribution',
    'keep_iptc',
    'layer_apply',
    'lossy',
    'no_overflow',
    'preserve_transparency',
    'png8',
    'png24',
    'png32',
    'progressive',
    'progressive:semi',
    'progressive:steep',
    'progressive:none',
    'rasterize',
    'region_relative',
    'relative',
    'strip_profile',
    'text_no_trim',
    'tiff8_lwz',
    'tiled',
];
var validate = {
    width: function (value) {
        return invariant(isNumber(value) || (typeof value === 'string' && value.match(/^auto(?:$|(?::.+))/)), 'width', value, "should be a number, 'auto', or a string starting with 'auto:'");
    },
    height: function (value) {
        return invariant(isNumber(value), 'height', value, 'should be a number');
    },
    crop: function (value) {
        return invariant(value && includes(value, cropOptions), 'crop', value, shouldBeOneOf(cropOptions));
    },
    aspectRatio: function (value) {
        return invariant(isNumber(value) || (typeof value === 'string' && value.match(/^\d+:\d+$/)), 'aspectRatio', value, 'should be a number or have the form x:y');
    },
    gravity: function (value) {
        return invariant(includes(value, gravityOptions) || value === 'auto' || startsWith('auto:', value), 'gravity', value, shouldBeOneOf(gravityOptions) + ", 'auto', or a string starting with 'auto:'");
    },
    zoom: function (value) { return invariant(isNumber(value), 'zoom', value, 'should be a number'); },
    x: function (value) { return invariant(isNumber(value), 'x', value, 'should be a number'); },
    y: function (value) { return invariant(isNumber(value), 'y', value, 'should be a number'); },
    format: function (value) {
        return invariant(includes(value, __spreadArray(['auto'], formatOptions)), 'fetchFormat', value, shouldBeOneOf(__spreadArray(['auto'], formatOptions)) + " or 'auto'");
    },
    fetchFormat: function (value) {
        this.format(value);
    },
    quality: function (value) {
        return invariant((isNumber(value) && 1 <= +value && +value <= 100) ||
            (typeof value === 'string' &&
                (value.match(/^\d+:\d+$/) || includes(value, qualityOptions))), 'quality', value, shouldBeOneOf(formatOptions) + ", a number between 1 and 100, or have the form x:y");
    },
    radius: function (value) {
        return invariant(isNumber(value) ||
            value === 'max' ||
            (typeof value === 'string' && value.match(/^\d+(?::\d+){0,3}$/)), 'radius', value, "should be a number, 'max' or have the form x[:y[:z[:u]]]");
    },
    angle: function (value) {
        return invariant(isNumber(value) || (typeof value === 'string' && includes(value, angleOptions)), 'angle', value, shouldBeOneOf(angleOptions) + " or a number");
    },
    effect: function () {
    },
    opacity: function (value) {
        return invariant(isNumber(value) && 0 <= +value && +value <= 100, 'opacity', value, 'should be a number between 0 and 100');
    },
    border: function (value) {
        return invariant(typeof value === 'string' &&
            value.match(new RegExp("^\\d+px_solid_" + colorRegex.toString().replace(/[/^i]/g, '') + "$", 'i')), 'border', value, "should be an object with 'width' & 'color' or a string of the form 'width_style_color'");
    },
    borderObjectColor: function (value) {
        return invariant('color' in value, 'border', JSON.stringify(value), "should contain a 'color' property when provided as an object");
    },
    background: function (value) {
        return invariant(includes(value, backgroundOptions) || value.match(colorRegex), 'background', value, shouldBeOneOf(backgroundOptions) + " or a color");
    },
    overlay: function () {
    },
    underlay: function () {
    },
    textCaptionStyles: function (value) {
        var fontFamily = value.fontFamily, fontSize = value.fontSize, fontWeight = value.fontWeight, fontStyle = value.fontStyle, textDecoration = value.textDecoration, textAlign = value.textAlign, stroke = value.stroke, letterSpacing = value.letterSpacing, lineSpacing = value.lineSpacing;
        invariant(fontFamily && fontSize && isNumber(fontSize), 'text caption', JSON.stringify(value), "required options are 'fontFamily' and 'fontSize'", '/image_transformations#adding_text_captions');
        invariant((!fontWeight || includes(fontWeight, ['normal', 'bold'])) &&
            (!fontStyle || includes(fontStyle, ['normal', 'italic'])) &&
            (!textDecoration || includes(textDecoration, ['none', 'underline', 'strikethrough'])) &&
            (!textAlign ||
                includes(textAlign, ['left', 'center', 'right', 'start', 'end', 'justify'])) &&
            (!stroke || includes(stroke, ['none', 'stroke'])) &&
            (!letterSpacing || isNumber(letterSpacing)) &&
            (!lineSpacing || isNumber(lineSpacing)), 'text caption', JSON.stringify(value), "options are invalid", '/image_transformations#adding_text_captions');
    },
    defaultImage: function (value) {
        return invariant(value.match(/^[\w+-]+\.[\w]{3,4}$/) &&
            includes(value.substr(value.indexOf('.') + 1), formatOptions), 'defaultImage', value, "must include a file extension which " + shouldBeOneOf(formatOptions));
    },
    delay: function (value) {
        return invariant(isNumber(value) && value >= 0, 'delay', value, "must be a positive number");
    },
    color: function (value) {
        return invariant(value.match(colorRegex), 'color', value, "must be a named color, short #rgb[a] or long #rrggbb[aa] hex value");
    },
    colorSpace: function (value) {
        return invariant(includes(value, colorSpaceOptions) || value.match(/^icc:[\w+-]+\.[\w]{3,4}$/), 'colorSpace', value, shouldBeOneOf(colorSpaceOptions) + " or 'icc:(public_id)'");
    },
    dpr: function (value) {
        return invariant(value === 'auto' || (isNumber(value) && value > 0), 'dpr', value, 'should be `auto` or a number greater than 0');
    },
    page: function (value) {
        return invariant(isNumber(value) && value > 0, 'page', value, 'should be an integer greater than 0');
    },
    density: function (value) {
        return invariant(isNumber(value) && 0 < value && value <= 300, 'density', value, 'should be a number greater than 0 up to 300');
    },
    flags: function (value) {
        invariant((Array.isArray(value) ? value : value.split('.')).every(function (flag) {
            return includes(flag, flagOptions);
        }), 'flags', JSON.stringify(value), shouldBeOneOf(flagOptions) + ", an array of options or '.' separated options");
    },
};
var urlParameters = {
    width: 'w_',
    height: 'h_',
    crop: 'c_',
    aspectRatio: 'ar_',
    gravity: 'g_',
    zoom: 'z_',
    x: 'x_',
    y: 'y_',
    fetchFormat: 'f_',
    format: 'f_',
    quality: 'q_',
    radius: 'r_',
    angle: 'a_',
    effect: 'e_',
    opacity: 'o_',
    border: 'bo_',
    background: 'b_',
    overlay: 'l_',
    underlay: 'u_',
    defaultImage: 'd_',
    delay: 'dl_',
    color: 'co_',
    colorSpace: 'cs_',
    dpr: 'dpr_',
    page: 'pg_',
    density: 'dn_',
    flags: 'fl_',
};
var compileTextCaption = function (value) {
    var publicIdStringStyle = value.publicId;
    if (!publicIdStringStyle) {
        if (process.env.NODE_ENV !== 'production') {
            validate.textCaptionStyles(value);
        }
        var letterSpacing = value.letterSpacing, lineSpacing = value.lineSpacing;
        publicIdStringStyle = [
            value.fontFamily && encodeURIComponent(value.fontFamily),
            value.fontSize,
            value.fontWeight,
            value.fontStyle,
            value.textDecoration,
            value.textAlign,
            value.stroke,
            letterSpacing ? "letter_spacing_" + letterSpacing : 0,
            lineSpacing ? "line_spacing_" + lineSpacing : 0,
        ]
            .filter(function (option) { return option && option !== 'normal' && option !== 'none'; })
            .join('_');
    }
    return "text:" + publicIdStringStyle + ":" + encodeURIComponent(value.text);
};
var compileBorder = function (value) {
    if (process.env.NODE_ENV !== 'production') {
        validate.borderObjectColor(value);
    }
    if (typeof value.width !== 'string' || value.width.substr(value.width.length - 2, 2) !== 'px') {
        value.width = (value.width || 1) + 'px';
    }
    return value.width + '_solid_' + value.color;
};
export function compileImageParameter(parameter, value) {
    if (value == null) {
        return false;
    }
    switch (parameter) {
        case 'flags':
            value = Array.isArray(value) ? value.join('.') : value;
            break;
        case 'overlay':
        case 'underlay':
            if (typeof value === 'object') {
                value = compileTextCaption(value);
            }
            break;
        case 'border':
            if (typeof value === 'object') {
                value = compileBorder(value);
            }
            value = value.toString().toLowerCase().replace('#', 'rgb:');
            break;
        case 'color':
        case 'background':
            value = value.toString().replace('#', 'rgb:');
    }
    if (process.env.NODE_ENV !== 'production') {
        if (!validate[parameter]) {
            throw new Error("Cloudinary Image :: unknown transform parameter provided: '" + parameter + "'");
        }
        validate[parameter](value);
    }
    return urlParameters[parameter] + value;
}
var compileImageTransform = function (transform) {
    return Object.keys(transform)
        .map(function (param) { return compileImageParameter(param, transform[param]); })
        .filter(Boolean)
        .join(',');
};
export var compileImageTransforms = function (transform, defaultTransform) {
    var compiledTransform = Array.isArray(transform)
        ? transform.map(compileImageTransform).join('/')
        : compileImageTransform(__assign(__assign({}, defaultTransform), transform));
    return compiledTransform ? "/" + compiledTransform : '';
};
